import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const Wrap = styled.div`
  position: relative;
  height: ${(p) => p.$height || '220px'};
  min-height: ${(p) => (p.$height === '100%' ? '100%' : 'auto')};
  background: #e8eef3;
  overflow: hidden;
`;

const Chrome = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
  z-index: 2;

  .dots {
    display: flex;
    gap: 5px;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;

      &:nth-child(1) {
        background: #f87171;
      }
      &:nth-child(2) {
        background: #fbbf24;
      }
      &:nth-child(3) {
        background: #4ade80;
      }
    }
  }

  .url {
    flex: 1;
    font-size: 0.68rem;
    color: #64748b;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 4px 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ui-monospace, monospace;
  }
`;

const Viewport = styled.div`
  position: relative;
  height: ${(p) => (p.$frameless ? '100%' : 'calc(100% - 37px)')};
  background: #fff;
  overflow: hidden;
`;

const Shot = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
  opacity: ${(p) => (p.$loaded ? 1 : 0)};
  transition: opacity 0.4s ease;
`;

const Loader = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(110deg, #f1f5f9 25%, #e8eef3 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }

  span {
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 600;
  }
`;

const Fallback = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${(p) => p.$bg || 'linear-gradient(160deg, #0e6b3c, #6dbb1a)'};

  i {
    font-size: 2rem;
    color: rgba(255, 255, 255, 0.9);
  }

  span {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 600;
  }
`;

/** Fast screenshot service (replaces WordPress mshots placeholder). */
export function getProjectScreenshotUrl(liveUrl) {
  if (!liveUrl || liveUrl === '#') return null;
  return `https://image.thum.io/get/width/1200/crop/1600/noanimate/${liveUrl}`;
}

async function fetchMicrolinkScreenshot(liveUrl) {
  const res = await fetch(
    `https://api.microlink.io/?url=${encodeURIComponent(liveUrl)}&screenshot=true&meta=false`
  );
  if (!res.ok) return null;
  const json = await res.json();
  return json?.data?.screenshot?.url || null;
}

/**
 * Browser frame showing the real site homepage (local image, then live screenshot APIs).
 */
const ProjectSitePreview = ({
  liveUrl,
  previewImage,
  domain,
  title,
  icon = 'fas fa-globe',
  gradient,
  height,
  frameless = false,
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [remoteUrl, setRemoteUrl] = useState(null);
  const [sourceIndex, setSourceIndex] = useState(0);
  const [exhausted, setExhausted] = useState(false);

  const sources = useMemo(() => {
    const list = [];
    if (previewImage) list.push(previewImage);
    const thum = getProjectScreenshotUrl(liveUrl);
    if (thum) list.push(thum);
    return list;
  }, [previewImage, liveUrl]);

  const activeSrc = exhausted ? null : remoteUrl || sources[sourceIndex] || null;
  const hasLiveUrl = liveUrl && liveUrl !== '#';
  const showFallback = (!hasLiveUrl && !previewImage) || exhausted;
  const showLoader = activeSrc && !imgLoaded && !imgFailed && !showFallback;

  useEffect(() => {
    setImgLoaded(false);
    setImgFailed(false);
    setRemoteUrl(null);
    setSourceIndex(0);
    setExhausted(false);
  }, [previewImage, liveUrl]);

  useEffect(() => {
    if (!hasLiveUrl || previewImage || remoteUrl) return undefined;
    if (!imgFailed) return undefined;
    if (sourceIndex < sources.length - 1) {
      setImgFailed(false);
      setImgLoaded(false);
      setSourceIndex((i) => i + 1);
      return undefined;
    }

    let cancelled = false;
    fetchMicrolinkScreenshot(liveUrl).then((url) => {
      if (cancelled) return;
      if (!url) {
        setExhausted(true);
        return;
      }
      setRemoteUrl(url);
      setImgFailed(false);
      setImgLoaded(false);
    });
    return () => {
      cancelled = true;
    };
  }, [imgFailed, hasLiveUrl, liveUrl, previewImage, remoteUrl, sourceIndex, sources.length]);

  useEffect(() => {
    if (!previewImage || !imgFailed || remoteUrl || sourceIndex > 0) return;
    setExhausted(true);
  }, [previewImage, imgFailed, remoteUrl, sourceIndex]);

  return (
    <Wrap $height={height}>
      {!frameless && (
        <Chrome>
          <div className="dots">
            <span /><span /><span />
          </div>
          <div className="url">{domain || liveUrl || 'Preview'}</div>
        </Chrome>
      )}
      <Viewport $frameless={frameless}>
        {showLoader && <Loader><span>Loading preview…</span></Loader>}

        {activeSrc && !showFallback && (
          <Shot
            key={activeSrc}
            src={activeSrc}
            alt={`${title} homepage`}
            loading="lazy"
            $loaded={imgLoaded}
            onLoad={() => setImgLoaded(true)}
            onError={() => {
              if (remoteUrl) {
                setExhausted(true);
                return;
              }
              setImgFailed(true);
            }}
          />
        )}

        {showFallback && (
          <Fallback $bg={gradient}>
            <i className={icon} />
            <span>{title}</span>
          </Fallback>
        )}
      </Viewport>
    </Wrap>
  );
};

export default ProjectSitePreview;
